/**
 * VehicleCascadingDropdown - Kaskadierendes Fahrzeug-Auswahlmenü
 * 
 * Animiertes, schrittweises Dropdown für:
 * 1. Hersteller
 * 2. Modell
 * 3. Generation/Interne Bezeichnung (B5, G20, etc.)
 * 4. Baujahr
 * 5. Motorisierung
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Search, Car, Calendar, Settings, Zap, Loader2, Check, AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Step Container
const AnimatedStep = ({ isVisible, isActive, isCompleted, delay = 0, children }) => {
  const [shouldRender, setShouldRender] = useState(false);
  
  useEffect(() => {
    let timer;
    if (isVisible) {
      timer = setTimeout(() => setShouldRender(true), delay);
    } else {
      timer = setTimeout(() => setShouldRender(false), 0);
    }
    return () => clearTimeout(timer);
  }, [isVisible, delay]);
  
  if (!shouldRender) return null;
  
  return (
    <div 
      className={cn(
        "transform transition-all duration-500 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4",
        isCompleted ? "opacity-60" : "opacity-100"
      )}
      style={{
        borderLeft: isActive ? '3px solid #7CD4FD' : isCompleted ? '3px solid #22C55E' : '3px solid transparent',
        paddingLeft: '16px',
        marginLeft: '8px'
      }}
    >
      {children}
    </div>
  );
};

// Searchable Dropdown Component
const SearchableDropdown = ({ 
  label, 
  icon: Icon, 
  options, 
  value, 
  onChange, 
  placeholder,
  isLoading,
  disabled,
  displayKey = 'name',
  valueKey = 'name',
  renderOption
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const filteredOptions = options.filter(opt => {
    const displayValue = typeof opt === 'string' ? opt : opt[displayKey];
    return displayValue?.toLowerCase().includes(search.toLowerCase());
  });
  
  const selectedDisplay = () => {
    if (!value) return null;
    const selected = options.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt[valueKey];
      return optValue === value;
    });
    return typeof selected === 'string' ? selected : selected?.[displayKey];
  };
  
  return (
    <div className="relative">
      <Label className="text-gray-300 flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-[#7CD4FD]" />}
        {label}
      </Label>
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className={cn(
          "w-full flex items-center justify-between px-4 py-3 rounded-lg",
          "bg-[#1A1F2E] border border-[#2A3441] text-left",
          "transition-all duration-200",
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#7CD4FD]/50 cursor-pointer",
          isOpen && "border-[#7CD4FD] ring-2 ring-[#7CD4FD]/20"
        )}
      >
        <span className={value ? "text-white" : "text-gray-500"}>
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Laden...
            </span>
          ) : (
            selectedDisplay() || placeholder
          )}
        </span>
        <ChevronDown className={cn(
          "w-5 h-5 text-gray-400 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-50 w-full mt-2 py-2 bg-[#1A1F2E] border border-[#2A3441] rounded-lg shadow-xl"
          style={{ maxHeight: '300px', overflowY: 'auto' }}
        >
          {/* Search Input */}
          <div className="px-3 pb-2 border-b border-[#2A3441]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#0F1318] border-[#2A3441] text-white"
                autoFocus
              />
            </div>
          </div>
          
          {/* Options */}
          <div className="py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-gray-500 text-center">
                Keine Ergebnisse
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const optValue = typeof opt === 'string' ? opt : opt[valueKey];
                const optDisplay = typeof opt === 'string' ? opt : opt[displayKey];
                const isSelected = optValue === value;
                
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      onChange(optValue, opt);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      "w-full px-4 py-2.5 text-left flex items-center justify-between",
                      "transition-colors duration-150",
                      isSelected 
                        ? "bg-[#7CD4FD]/10 text-[#7CD4FD]" 
                        : "text-gray-300 hover:bg-[#2A3441]"
                    )}
                  >
                    {renderOption ? renderOption(opt) : optDisplay}
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      
      {/* Click outside handler */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearch('');
          }}
        />
      )}
    </div>
  );
};

// Main Component
export const VehicleCascadingDropdown = ({ 
  onSelectionComplete, 
  initialValues = {},
  showEngines = true,
  compact = false 
}) => {
  // Selection State
  const [manufacturer, setManufacturer] = useState(initialValues.manufacturer || '');
  const [model, setModel] = useState(initialValues.model || '');
  const [generation, setGeneration] = useState(initialValues.generation || '');
  const [year, setYear] = useState(initialValues.year || '');
  const [engine, setEngine] = useState(initialValues.engine || '');
  
  // Data State
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [years, setYears] = useState([]);
  const [engines, setEngines] = useState([]);
  
  // Loading State
  const [loading, setLoading] = useState({
    manufacturers: false,
    models: false,
    generations: false,
    years: false,
    engines: false
  });
  
  // Error State
  const [errors, setErrors] = useState({});
  
  // Helper to get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load Manufacturers on mount
  useEffect(() => {
    const loadManufacturers = async () => {
      setLoading(prev => ({ ...prev, manufacturers: true }));
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/dropdown/manufacturers`, { headers: getHeaders() });
        setManufacturers(response.data.manufacturers || []);
      } catch (error) {
        console.error('Failed to load manufacturers:', error);
        setErrors(prev => ({ ...prev, manufacturers: 'Fehler beim Laden' }));
      } finally {
        setLoading(prev => ({ ...prev, manufacturers: false }));
      }
    };
    loadManufacturers();
  }, []);
  
  // Load Models when manufacturer changes
  useEffect(() => {
    if (!manufacturer) {
      setModels([]);
      setModel('');
      return;
    }
    
    const loadModels = async () => {
      setLoading(prev => ({ ...prev, models: true }));
      setModel('');
      setGeneration('');
      setYear('');
      setEngine('');
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/dropdown/models/${encodeURIComponent(manufacturer)}`, { headers: getHeaders() });
        setModels(response.data.models || []);
      } catch (error) {
        console.error('Failed to load models:', error);
        setErrors(prev => ({ ...prev, models: 'Fehler beim Laden' }));
      } finally {
        setLoading(prev => ({ ...prev, models: false }));
      }
    };
    loadModels();
  }, [manufacturer]);
  
  // Load Generations when model changes
  useEffect(() => {
    if (!model) {
      setGenerations([]);
      setGeneration('');
      return;
    }
    
    const loadGenerations = async () => {
      setLoading(prev => ({ ...prev, generations: true }));
      setGeneration('');
      setYear('');
      setEngine('');
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/dropdown/generations/${encodeURIComponent(manufacturer)}/${encodeURIComponent(model)}`, { headers: getHeaders() });
        setGenerations(response.data.generations || []);
      } catch (error) {
        console.error('Failed to load generations:', error);
      } finally {
        setLoading(prev => ({ ...prev, generations: false }));
      }
    };
    loadGenerations();
  }, [manufacturer, model]);
  
  // Load Years when generation changes
  useEffect(() => {
    if (!generation) {
      setYears([]);
      setYear('');
      return;
    }
    
    const loadYears = async () => {
      setLoading(prev => ({ ...prev, years: true }));
      setYear('');
      setEngine('');
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/dropdown/years/${encodeURIComponent(manufacturer)}/${encodeURIComponent(model)}/${encodeURIComponent(generation)}`, { headers: getHeaders() });
        setYears(response.data.years || []);
      } catch (error) {
        console.error('Failed to load years:', error);
      } finally {
        setLoading(prev => ({ ...prev, years: false }));
      }
    };
    loadYears();
  }, [manufacturer, model, generation]);
  
  // Load Engines when year changes
  useEffect(() => {
    if (!year || !showEngines) {
      setEngines([]);
      setEngine('');
      return;
    }
    
    const loadEngines = async () => {
      setLoading(prev => ({ ...prev, engines: true }));
      setEngine('');
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicle-catalog/dropdown/engines/${encodeURIComponent(manufacturer)}/${encodeURIComponent(model)}/${encodeURIComponent(generation)}?year=${year}`, { headers: getHeaders() });
        setEngines(response.data.engines || []);
      } catch (error) {
        console.error('Failed to load engines:', error);
      } finally {
        setLoading(prev => ({ ...prev, engines: false }));
      }
    };
    loadEngines();
  }, [manufacturer, model, generation, year, showEngines]);
  
  // Notify parent of selection
  useEffect(() => {
    if (onSelectionComplete) {
      const isComplete = manufacturer && model && (
        !showEngines ? (generation && year) : (generation && year && engine)
      );
      
      onSelectionComplete({
        manufacturer,
        model,
        generation,
        year: year ? parseInt(year) : null,
        engine,
        isComplete,
        displayName: manufacturer && model ? `${manufacturer} ${model}${generation ? ` ${generation}` : ''}${year ? ` (${year})` : ''}${engine ? ` - ${engine}` : ''}` : ''
      });
    }
  }, [manufacturer, model, generation, year, engine, showEngines, onSelectionComplete]);
  
  // Get completion percentage
  const getCompletionPercentage = () => {
    let steps = showEngines ? 5 : 4;
    let completed = 0;
    if (manufacturer) completed++;
    if (model) completed++;
    if (generation) completed++;
    if (year) completed++;
    if (showEngines && engine) completed++;
    return Math.round((completed / steps) * 100);
  };
  
  return (
    <div className={cn("space-y-4", compact && "space-y-3")}>
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Fahrzeugauswahl</span>
          <span>{getCompletionPercentage()}% abgeschlossen</span>
        </div>
        <div className="h-1.5 bg-[#1A1F2E] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] transition-all duration-500 ease-out"
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>
      </div>
      
      {/* Step 1: Hersteller */}
      <AnimatedStep isVisible={true} isActive={!manufacturer} isCompleted={!!manufacturer} delay={0}>
        <SearchableDropdown
          label="Hersteller"
          icon={Car}
          options={manufacturers}
          value={manufacturer}
          onChange={(val) => setManufacturer(val)}
          placeholder="Hersteller auswählen..."
          isLoading={loading.manufacturers}
          disabled={false}
        />
      </AnimatedStep>
      
      {/* Step 2: Modell */}
      <AnimatedStep isVisible={!!manufacturer} isActive={manufacturer && !model} isCompleted={!!model} delay={100}>
        <SearchableDropdown
          label="Modell"
          icon={Car}
          options={models}
          value={model}
          onChange={(val) => setModel(val)}
          placeholder="Modell auswählen..."
          isLoading={loading.models}
          disabled={!manufacturer}
          renderOption={(opt) => (
            <div className="flex justify-between items-center">
              <span>{opt.name}</span>
              {opt.generations_count > 0 && (
                <span className="text-xs text-gray-500">{opt.generations_count} Generationen</span>
              )}
            </div>
          )}
        />
      </AnimatedStep>
      
      {/* Step 3: Generation/Interne Bezeichnung */}
      <AnimatedStep isVisible={!!model} isActive={model && !generation} isCompleted={!!generation} delay={200}>
        {generations.length > 0 ? (
          <SearchableDropdown
            label="Generation / Interne Bezeichnung"
            icon={Settings}
            options={generations}
            value={generation}
            onChange={(val) => setGeneration(val)}
            placeholder="Generation auswählen..."
            isLoading={loading.generations}
            disabled={!model}
            displayKey="display_name"
            valueKey="code"
            renderOption={(gen) => (
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium">{gen.display_name || gen.code}</span>
                  {gen.type_code && <span className="text-gray-500 ml-2">({gen.type_code})</span>}
                </div>
                <span className="text-xs text-gray-500">{gen.year_start}-{gen.year_end}</span>
              </div>
            )}
          />
        ) : (
          <div className="p-4 bg-[#1A1F2E] border border-[#2A3441] rounded-lg">
            <div className="flex items-center gap-3 text-gray-400">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-sm">Keine Generationen-Daten verfügbar</p>
                <p className="text-xs text-gray-500">Bitte wählen Sie direkt ein Baujahr</p>
              </div>
            </div>
            {/* Fallback to manual year input */}
            <div className="mt-3">
              <SearchableDropdown
                label="Baujahr"
                icon={Calendar}
                options={Array.from({length: 26}, (_, i) => (2000 + i).toString())}
                value={year}
                onChange={(val) => {
                  setGeneration('manual');
                  setYear(val);
                }}
                placeholder="Baujahr wählen..."
                isLoading={false}
                disabled={false}
              />
            </div>
          </div>
        )}
      </AnimatedStep>
      
      {/* Step 4: Baujahr */}
      {generations.length > 0 && (
        <AnimatedStep isVisible={!!generation && generation !== 'manual'} isActive={generation && !year} isCompleted={!!year} delay={300}>
          <SearchableDropdown
            label="Baujahr"
            icon={Calendar}
            options={years.map(y => y.toString())}
            value={year}
            onChange={(val) => setYear(val)}
            placeholder="Baujahr auswählen..."
            isLoading={loading.years}
            disabled={!generation}
          />
        </AnimatedStep>
      )}
      
      {/* Step 5: Motorisierung (optional) */}
      {showEngines && (
        <AnimatedStep isVisible={!!year} isActive={year && !engine} isCompleted={!!engine} delay={400}>
          {engines.length > 0 ? (
            <SearchableDropdown
              label="Motorisierung"
              icon={Zap}
              options={engines}
              value={engine}
              onChange={(val) => setEngine(val)}
              placeholder="Motorisierung auswählen..."
              isLoading={loading.engines}
              disabled={!year}
              displayKey="display_name"
              valueKey="code"
              renderOption={(eng) => (
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">{eng.name}</span>
                    <span className="text-gray-500 ml-2">{eng.power_ps} PS</span>
                  </div>
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded",
                    eng.fuel_type === 'Benzin' ? "bg-green-500/20 text-green-400" :
                    eng.fuel_type === 'Diesel' ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-blue-500/20 text-blue-400"
                  )}>
                    {eng.fuel_type}
                  </span>
                </div>
              )}
            />
          ) : (
            <div className="p-4 bg-[#1A1F2E] border border-[#2A3441] rounded-lg">
              <div className="flex items-center gap-3 text-gray-400">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="text-sm">Keine Motordaten verfügbar</p>
                  <p className="text-xs text-gray-500">Die Auswahl ist abgeschlossen</p>
                </div>
              </div>
            </div>
          )}
        </AnimatedStep>
      )}
      
      {/* Selection Summary */}
      {manufacturer && model && (
        <div className="mt-6 p-4 bg-gradient-to-r from-[#1A1F2E] to-[#0F1318] border border-[#2A3441] rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Ausgewähltes Fahrzeug:</p>
          <p className="text-lg font-medium text-white">
            {manufacturer} {model}
            {generation && generation !== 'manual' && <span className="text-[#7CD4FD]"> {generation}</span>}
            {year && <span className="text-gray-400"> ({year})</span>}
          </p>
          {engine && (
            <p className="text-sm text-gray-400 mt-1">
              Motor: <span className="text-white">{engines.find(e => e.code === engine)?.display_name || engine}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleCascadingDropdown;
